import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export type DomainStatus = "pending_verification" | "verifying" | "active" | "failed" | "disabled";

/** A DNS record the church adds where it bought its domain. */
export interface DomainRecord {
  readonly type: string;
  /** What most registrars ask for: the part before the church's domain (`www`). */
  readonly name: string;
  readonly value: string;
  /** `routing` is the one record every church adds; the others are Cloudflare's, and optional. */
  readonly purpose: "routing" | "ownership" | "certificate";
}

export interface CustomDomain {
  readonly id: string;
  readonly hostname: string;
  readonly status: DomainStatus;
  readonly isPrimary: boolean;
  readonly lastError: string | null;
  readonly lastCheckedAt: string | null;
  readonly liveSince: string | null;
  /** The address without its first label (`theirchurch.org`), which forwards to this one at the registrar. */
  readonly rootDomain: string;
  readonly records: readonly DomainRecord[];
}

/** Everything the Domain page shows (`custom_domain_setup`, ADR 0051). */
export interface DomainSetup {
  /** Where every church's CNAME points: `sites.<platform domain>`. */
  readonly target: string;
  /** The address HoW gave the church, which always works. */
  readonly platformHost: string | null;
  readonly domains: readonly CustomDomain[];
}

/** A failed call, with the database's error code and words kept so the page can say what went wrong. */
export class DomainError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
  ) {
    super(message);
  }
}

function failed(error: { message: string; code?: string } | null): void {
  if (error !== null) throw new DomainError(error.message, error.code);
}

/** True while a domain is on its way to live, when the page keeps itself up to date. */
export function isSettling(domain: CustomDomain): boolean {
  return domain.status === "pending_verification" || domain.status === "verifying";
}

/** A church connects at most this many addresses (`request_custom_domain`). */
export const MAX_DOMAINS = 5;

export function useDomainSetup() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["domain-setup", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<DomainSetup> => {
      const { data, error } = await supabase.rpc("custom_domain_setup", {
        p_tenant_id: tenantId ?? "",
      });
      failed(error);
      return data as unknown as DomainSetup;
    },
    // HoW checks a waiting domain every minute; the page follows without anyone pressing anything.
    refetchInterval: (query) =>
      query.state.data?.domains.some(isSettling) === true ? 15_000 : false,
  });
}

export function useRequestDomain() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (hostname: string): Promise<void> => {
      const { error } = await supabase.rpc("request_custom_domain", {
        p_tenant_id: tenantId ?? "",
        p_hostname: hostname,
      });
      failed(error);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ["domain-setup", tenantId] }),
  });
}

export function useRecheckDomain() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (domainId: string): Promise<void> => {
      const { error } = await supabase.rpc("recheck_custom_domain", { p_domain_id: domainId });
      failed(error);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ["domain-setup", tenantId] }),
  });
}

export function useRemoveDomain() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (domainId: string): Promise<void> => {
      const { error } = await supabase.rpc("remove_tenant_domain", { p_domain_id: domainId });
      failed(error);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ["domain-setup", tenantId] }),
  });
}

/**
 * Tidies what a person types into the address the database will keep: no scheme, no path, lower case. A bare
 * domain gets `www.` suggested by the page, not added silently.
 */
export function tidyHostname(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");
}

const HOSTNAME = /^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** Why the typed address cannot be sent, or null when it can. The database checks all of it again. */
export function hostnameProblem(input: string): string | null {
  const host = tidyHostname(input);
  if (host === "") return null;
  if (!HOSTNAME.test(host)) return "Enter an address such as www.yourchurch.org.";
  // HoW's own addresses, in either environment, as the database and the sign-in screen refuse them.
  if (host === "dcxlogic.com" || host.endsWith(".dcxlogic.com")) {
    return "That address is one of HoW's own. Enter your church's domain.";
  }
  if (host.split(".").length < 3) {
    return `Enter it with www: www.${host}. The address without www forwards to it (see below).`;
  }
  return null;
}

/** What a failed domain call means to the person who made it. */
export function domainErrorMessage(error: unknown): string {
  const code = error instanceof DomainError ? error.code : undefined;
  if (code === "42501") return "Only your church's owner can connect a domain.";
  if (code === "23505") return "That address is already connected to a HoW website.";
  // The database's own sentences for a broken rule are written for the person (a root domain, a platform address).
  if (code === "23514" && error instanceof DomainError) {
    return error.message.charAt(0).toUpperCase() + error.message.slice(1) + ".";
  }
  if (code === "P0002") return "That address is no longer here. Refresh the page.";
  return "Something went wrong. Try again.";
}

export const DOMAIN_STATUS_LABELS: Record<DomainStatus, string> = {
  pending_verification: "Waiting for your DNS record",
  verifying: "Issuing the certificate",
  active: "Live",
  failed: "Needs attention",
  disabled: "Turned off",
};

/**
 * When HoW last looked, as the person's own clock would say it: the time today, the date and time before that. The
 * page passes no locale, so it reads the way the person's browser writes times; a test passes one (#405).
 */
export function checkedAt(iso: string, now: Date = new Date(), locale?: string): string {
  const at = new Date(iso);
  const sameDay = at.toDateString() === now.toDateString();
  return new Intl.DateTimeFormat(locale, {
    ...(sameDay ? {} : { month: "short", day: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  }).format(at);
}
